import { useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { FeedbackBanner } from "@/core/components/feedback-banner";
import { HiddenWhenSpiffOnly } from "@/core/components/hidden-when-spiff-only";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography/typography";
import {
  HeaderStack,
  MainCard,
  MainStack,
} from "@/business-settings/common/business-settings-page";
import { PageHeader } from "@/business-settings/common/page-header";
import { cn } from "@/core/utils/utils";
import {
  useGetAchAccount,
  useGetAccountSummary,
} from "@/internal-transactions/query";
import { useGetMyUser } from "@/user/query";
import { isAdminOrOwner, isAdminOrOwnerOrManager } from "@/user/utils";
import AutoRefillBalanceModal from "./auto-refill-balance-modal";
import DepositWithdrawModal from "./deposit-withdraw-modal";
import { EmptyPaymentState } from "./empty-payment-state";
import { NewBankDetailsModal } from "./new-bank-details-modal";
import { PaymentsActions } from "./payments-actions";
import PaymentsSummary from "./payments-summary";
import PaymentsTable from "./payments-table";

const PaymentsContent = () => {
  const { businessId } = useParams();
  const [depositModalMode, setDepositModalMode] = useState<
    "deposit" | "withdraw" | undefined
  >(undefined);
  const [newBankDetailsModalOpen, setNewBankDetailsModalOpen] = useState(false);
  const [autoRefillBalanceModalOpen, setAutoRefillBalanceModalOpen] =
    useState(false);

  const { data: userInfo } = useGetMyUser();
  const { data: achAccount, isLoading: isAchAccountLoading } =
    useGetAchAccount(businessId);
  const { data: accountSummary } = useGetAccountSummary(businessId);

  const showPayments = isAdminOrOwnerOrManager(userInfo?.role);
  const canActOnPayments = isAdminOrOwner(userInfo?.role);
  const hasAutoRefill = accountSummary?.settings?.auto_refill_active;

  if (!showPayments) {
    return <Navigate to={`/business-settings/${businessId}/`} replace />;
  }

  const hasAchAccount = !!achAccount;

  return (
    <>
      <Stack className="mr-6" gap="sm" fullWidth>
        <HeaderStack>
          <PageHeader>Payments</PageHeader>
          {canActOnPayments && (
            <PaymentsActions
              businessId={businessId}
              onDeposit={() => setDepositModalMode("deposit")}
              onWithdraw={() => setDepositModalMode("withdraw")}
              onAutoRefillBalance={() => setAutoRefillBalanceModalOpen(true)}
              onEditBankDetails={() => setNewBankDetailsModalOpen(true)}
              onAddBankDetails={() => setNewBankDetailsModalOpen(true)}
            />
          )}
        </HeaderStack>

        {hasAutoRefill && (
          <FeedbackBanner
            variant="info"
            className="w-[calc(100vw-281px)] min-w-[800px] items-center p-2"
          >
            <Typography variant="info" className="text-md">
              Your account is set up for auto-refill. You can edit this in your{" "}
              <button
                type="button"
                className="cursor-pointer underline"
                onClick={() => setAutoRefillBalanceModalOpen(true)}
              >
                auto-refill settings
              </button>
              .
            </Typography>
          </FeedbackBanner>
        )}

        <MainCard className={cn(hasAutoRefill && "h-[calc(100vh-218px)]")}>
          <MainStack className="pt-6">
            {!isAchAccountLoading && !hasAchAccount ? (
              <EmptyPaymentState
                className="mt-6"
                onAddBankDetails={() => setNewBankDetailsModalOpen(true)}
              />
            ) : (
              <>
                <PaymentsSummary
                  businessId={businessId}
                  onAutoRefillClick={() => setAutoRefillBalanceModalOpen(true)}
                />
                <PaymentsTable />
              </>
            )}
          </MainStack>
        </MainCard>
      </Stack>
      <DepositWithdrawModal
        mode={depositModalMode}
        onOpenChange={(open) =>
          setDepositModalMode(open ? "deposit" : undefined)
        }
        businessId={businessId}
      />
      <NewBankDetailsModal
        isOpen={newBankDetailsModalOpen}
        onOpenChange={setNewBankDetailsModalOpen}
        businessId={businessId}
      />
      <AutoRefillBalanceModal
        isOpen={autoRefillBalanceModalOpen}
        onOpenChange={setAutoRefillBalanceModalOpen}
        businessId={businessId}
        currentSettings={accountSummary?.settings}
      />
    </>
  );
};

const Payments = () => (
  <HiddenWhenSpiffOnly fallbackPath="../">
    <PaymentsContent />
  </HiddenWhenSpiffOnly>
);

export default Payments;
